import { useMediaQuery } from '@withjoy/joykit/utils';
import { useCallback, useMemo } from 'react';
import type { ProductGridHighlightsProps } from './ProductGridHighlights';
import { PRODUCT_GRID_BREAKPOINTS } from '../../../../Catalog.constants';
import { useRouteMatch } from '@react-router';
import { useEventTypeConfig } from '@shared/utils/eventType';
import { useFeatureValue } from '@shared/core/featureFlags';
import { HIGHLIGHTS } from './ProductGridHighlights.constants';
import type { MinimalProductWithDetails } from '../../../../utils/createTelemetryPayload';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';

export const useProductGridHighlights = () => {
  const mobileMediaQuery = useMediaQuery(theme => {
    return theme.mediaQueries.between(0, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[1] });
  });
  const tabletMediaQuery = useMediaQuery(theme => theme.mediaQueries.between({ breakpointAlias: PRODUCT_GRID_BREAKPOINTS[1] }, { breakpointAlias: PRODUCT_GRID_BREAKPOINTS[2] }));

  const routeMatch = useRouteMatch({ path: Object.keys(HIGHLIGHTS) }) as { path: keyof typeof HIGHLIGHTS } | null;

  const { config } = useEventTypeConfig();
  const { payload } = useFeatureValue('shopSubCatHighlightsExperiment');
  const { trackProductTileClickAction } = useCatalogLoggingContext();

  const trackTileClick = useCallback(
    ({ product, position }: { product: MinimalProductWithDetails; position: number }) => {
      // numeric position indexes aren't as helpful as the string values
      const method = <'productTileGood' | 'productTileBetter' | 'productTileBest'>['productTileGood', 'productTileBetter', 'productTileBest'][position];

      trackProductTileClickAction({
        product,
        position,
        page: 'subcategory',
        component: 'productGridHighlights',
        method,
      });
    },
    [trackProductTileClickAction],
  );

  const subCatHighlightsVisible = useMemo(() => {
    return routeMatch && payload === true && config?.subCatHighlights;
  }, [routeMatch, config?.subCatHighlights, payload]);

  // "what the ...?"
  //   ProductGridHighlights is built to take up an antire row,
  //   and be injected at column 1 of its row within the flow of the ProductGrid,
  //   after two full rows of ProductTiles.
  //   this requires us to know the column count per media breakpoint,
  //   as expressed by nonZeroProductListStyles.gridTemplateColumns in 'ProductGrid.styles'.
  const subCatHighlightsIndex = useMemo<ProductGridHighlightsProps['positionIndex']>(() => {
    if (mobileMediaQuery) {
      return 4; // 2x 2-column rows
    }
    if (tabletMediaQuery) {
      return 6; // 2x 3-column rows
    }
    return 8; // 2x 4-column rows
  }, [mobileMediaQuery, tabletMediaQuery]);

  return {
    routeMatch,
    subCatHighlightsVisible,
    subCatHighlightsIndex,
    trackTileClick,
  };
};
